import { CompanyUser } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

type UseRemoveLicenseActionProps = {
    user: CompanyUser;
};

type UseRemoveLicenseActionData = {
    /** Whether this viewer can take this member's license away right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    removeLicense: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Taking a member's license away: whether this viewer may do it, why not when
 * they may not, and the call itself. Kept together so a caller can wire up a
 * button — disabled state, tooltip, spinner and click — from one hook.
 */
export function useRemoveLicenseAction(
    props: UseRemoveLicenseActionProps
): UseRemoveLicenseActionData {
    const { user } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const canManageLicences = user.capabilities.canManageLicences;
    const [isAllowed, reason] = useMemo(() => {
        // The API decides who is permitted to manage this member's licence —
        // including the members it won't let this viewer touch, such as a
        // company owner — and hands back a ready-to-show reason when they are
        // not.
        if (!canManageLicences.allowed) {
            return [
                false,
                canManageLicences.reason ??
                    t('actions.remove_license.reason_not_permitted')
            ];
        }

        if (!user.license) {
            return [false, t('actions.remove_license.reason_no_license')];
        }

        return [true, ''];
    }, [canManageLicences, user.license, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `PATCH /v3/users/{id}/licenses`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'NOT_SUBSCRIBED')) {
                return t('actions.remove_license.error_not_subscribed');
            }
            if (isApiError(error, 'NO_MORE_LICENSES')) {
                return t('actions.remove_license.error_no_more_licenses');
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('actions.remove_license.error_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.users
                .updateUserLicense(user.id, { license: null })
                .then((response) => response.data.data.user),
        onSuccess: (user) => {
            addOrUpdateCompanyUsersCache(queryClient, user);

            UIToast.success(t('actions.remove_license.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        removeLicense: () => mutation.mutateAsync().then(() => {}),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
